import React, { useState, useEffect } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { isValidEmail } from "utils/Validation/isValidEmail";
import { ErrorAlert } from "components/alert/ErrorAlert";
import { getLicenseEmailDefaultRecipients, sendLicenseEmail } from "../services/subscriptionService";

type SendLicenseEmailDialogProps = {
  open: boolean;
  subscriptionId: string;
  subscriptionName: string;
  handleClose: () => void;
  onSent?: () => void;
  initialRecipients?: { to: string[]; cc: string[]; bcc: string[] };
};

export function SendLicenseEmailDialog({
  open,
  subscriptionId,
  subscriptionName,
  handleClose,
  onSent,
  initialRecipients,
}: SendLicenseEmailDialogProps) {
  const [toRecipients, setToRecipients] = useState<string[]>([]);
  const [ccRecipients, setCcRecipients] = useState<string[]>([]);
  const [bccRecipients, setBccRecipients] = useState<string[]>([]);
  const [isReseller, setIsReseller] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown | undefined>();

  useEffect(() => {
    if (!open) {
      setToRecipients([]);
      setCcRecipients([]);
      setBccRecipients([]);
      setIsReseller(false);
      setWarnings([]);
      setError(undefined);
      return;
    }

    const hasInitialRecipients =
      initialRecipients &&
      (initialRecipients.to.length > 0 || initialRecipients.cc.length > 0 || initialRecipients.bcc.length > 0);
    if (hasInitialRecipients) {
      setToRecipients(initialRecipients.to);
      setCcRecipients(initialRecipients.cc);
      setBccRecipients(initialRecipients.bcc);
      return;
    }

    setRecipientsLoading(true);
    setError(undefined);

    getLicenseEmailDefaultRecipients(subscriptionId)
      .then((defaults) => {
        setToRecipients(defaults.to);
        setCcRecipients(defaults.cc);
        setIsReseller(defaults.isReseller);
        setWarnings(defaults.warnings);
      })
      .catch((e) => setError(e))
      .finally(() => setRecipientsLoading(false));
  }, [open, subscriptionId, initialRecipients]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(undefined);
    try {
      await sendLicenseEmail(subscriptionId, {
        to: toRecipients,
        cc: ccRecipients,
        bcc: bccRecipients,
      });
      handleClose();
      onSent?.();
    } catch (e) {
      setError(e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!submitting) handleClose();
  };

  const hasInvalidRecipients = [...toRecipients, ...ccRecipients, ...bccRecipients].some((v) => !isValidEmail(v));
  const canSubmit = toRecipients.length > 0 && !hasInvalidRecipients && !submitting && !recipientsLoading;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Send License Email — {subscriptionName}</DialogTitle>
      <DialogContent>
        {recipientsLoading ? (
          <Stack
            sx={{
              alignItems: "center",
              py: 3,
            }}
          >
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error !== undefined && <ErrorAlert error={error} />}
            {warnings.map((warning, i) => (
              <Alert key={i} severity="warning">
                {warning}
              </Alert>
            ))}
            {isReseller && (
              <Alert severity="info">Reseller deal — the payment reference line will be suppressed in the email.</Alert>
            )}
            <RecipientField label="To" value={toRecipients} onChange={setToRecipients} disabled={submitting} />
            <RecipientField label="CC" value={ccRecipients} onChange={setCcRecipients} disabled={submitting} />
            <RecipientField label="BCC" value={bccRecipients} onChange={setBccRecipients} disabled={submitting} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
          {submitting ? "Sending..." : "Send License Email"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface RecipientFieldProps {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  disabled: boolean;
}

function RecipientField({ label, value, onChange, disabled }: RecipientFieldProps) {
  const hasInvalid = value.some((v) => !isValidEmail(v));

  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]}
      value={value}
      onChange={(_, newValue) => onChange(newValue.filter((v): v is string => typeof v === "string"))}
      renderValue={(tagValues, getItemProps) =>
        tagValues.map((option, index) => (
          <Chip
            variant="outlined"
            label={option}
            size="small"
            color={isValidEmail(option) ? "default" : "error"}
            {...getItemProps({ index })}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={hasInvalid}
          helperText={hasInvalid ? "One or more addresses are invalid" : "Press Enter after each address to add it"}
        />
      )}
      disabled={disabled}
    />
  );
}
